import React from "react";
import swal from "@sweetalert/with-react";

export const validUploadedTypes = ["image/png"];
export const REQUIRED_WIDTH = 348;
export const REQUIRED_HEIGHT = 348;

export const validateImageDimensions = (
    file: File,
    width: number = REQUIRED_WIDTH,
    height: number = REQUIRED_HEIGHT
): Promise<boolean> => {
    return new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(img.width === width && img.height === height);
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(false);
        };

        img.src = objectUrl;
    });
};

export type UploadResponse = {
    error?: boolean;
    message?: string;
    data: string;
};

export interface QRCodeHandlerDeps {
    t: (key: string) => string;
    setDisplayImage: (value: string) => void;
    setQrCodeImage: (value: string) => void;
    setIsLoading: (value: boolean) => void;
    fetchGlobal: <T>(url: string, options?: RequestInit) => Promise<T>;
}

async function uploadQrCodeFile(
    file: File,
    deps: QRCodeHandlerDeps
): Promise<void> {
    const {
        t,
        setDisplayImage,
        setQrCodeImage,
        setIsLoading,
        fetchGlobal,
    } = deps;
    const apiUrl = `${process.env.REACT_APP_API_V2_URL}/upload/qr`;

    setIsLoading(true);
    try {
        if (!validUploadedTypes.includes(file.type)) {
            swal({
                icon: "error",
                content: <p>{t("Only PNG files are accepted")}!</p>,
                buttons: false,
                timer: 2000,
            });
            return;
        }

        const isValidDimensions = await validateImageDimensions(file);
        if (!isValidDimensions) {
            swal({
                icon: "error",
                content: (
                    <p>{t("Image dimensions must be 348 × 348 pixels")}!</p>
                ),
                buttons: false,
                timer: 2000,
            });
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === "string") {
                setDisplayImage(reader.result);
            }
        };
        reader.readAsDataURL(file);

        const formData = new FormData();
        formData.append("file", file, file.name);

        const result = await fetchGlobal<UploadResponse>(apiUrl, {
            body: formData,
            method: "PUT",
        });

        if (result.error) {
            throw new Error(result.message);
        }

        setQrCodeImage(result.data);
        setDisplayImage(result.data);

        swal({
            icon: "success",
            content: <p>{t("QR code loaded successfully.")}</p>,
            buttons: false,
            timer: 2000,
        });
    } catch (error) {
        swal({
            icon: "error",
            content: (
                <p>{t("An error occurred while uploading the QR code.")}</p>
            ),
            buttons: false,
            timer: 2000,
        });
    } finally {
        setIsLoading(false);
    }
}

export const createHandleFileChange = (deps: QRCodeHandlerDeps) => {
    return async (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileTarget = e.target?.files;
        if (fileTarget !== null && fileTarget.length > 0) {
            await uploadQrCodeFile(fileTarget[0], deps);
        }
    };
};

export const createHandleDrop = (deps: QRCodeHandlerDeps) => {
    return async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            await uploadQrCodeFile(e.dataTransfer.files[0], deps);
        }
    };
};

export const createHandleDeleteImage = (
    setQrCodeImage: (value: string) => void
) => {
    return () => {
        setQrCodeImage("");
    };
};
